import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { Box, Button, CircularProgress, Container, Typography } from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import LoginIcon from "@mui/icons-material/Login";
import AuthPromptDialog from "@/components/Auth/AuthPromptDialog";
import { activeRegionAtom } from "@/atoms/activeRegion";
import { activeCameraIdAtom } from "@/atoms/activeCamera";
import { usePageRedirect } from "@/hooks/usePageRedirect";
import { usePageView } from "@/hooks/usePageView";
import { useAuth } from "@/hooks/useAuth";
import { useRegions } from "@/hooks/useRegions";
import { findNearestRegion } from "@/hooks/useGeolocateRegion";
import {
  trackWelcomeArrival,
  trackWelcomeLocateError,
  trackWelcomeLocateSuccess,
  trackWelcomeSigninClick,
  trackWelcomeSkipClick,
} from "@/lib/analytics";
import { dismissWelcome } from "@/lib/welcomeFlag";
import { fontStack } from "@/theme";

/**
 * First-visit landing page. Replaces the old WelcomeModal with a real
 * page so it's linkable from email campaigns, shareable, and gives
 * returning users a cleaner re-engagement surface.
 *
 * Two primary CTAs — geolocate (jump to nearest break with a cam) or
 * Google sign-in — plus a tertiary "skip" that drops into the default
 * forecast view. Any CTA marks the welcome flag so we don't show the
 * page again unless the user clears storage or hits `/welcome` directly.
 *
 * Reads `utm_campaign` to swap headline copy for email-campaign
 * visitors and fires a GA `welcome_arrival` event with the campaign
 * slug so the funnel reads cleanly.
 */
export default function Welcome() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const readyForPageView = usePageRedirect(user ? "/" : null, loading);
  usePageView(readyForPageView);
  const [authOpen, setAuthOpen] = useState(false);
  const { data: regionsData } = useRegions();
  const [, setActiveRegion] = useRecoilState(activeRegionAtom);
  const [, setActiveCameraId] = useRecoilState(activeCameraIdAtom);

  const campaign = params.get("utm_campaign");
  const isLegacyCampaign =
    campaign === "legacy-welcome" || campaign === "legacy-followup";
  const wantsSignup = params.get("intent") === "signup";

  const [locateState, setLocateState] = useState<
    { status: "idle" } | { status: "locating" } | { status: "error"; message: string }
  >({ status: "idle" });

  const arrivalLogged = useRef(false);
  const signupPrompted = useRef(false);
  useEffect(() => {
    if (arrivalLogged.current) return;
    arrivalLogged.current = true;
    trackWelcomeArrival(campaign ?? "direct");
  }, [campaign]);

  useEffect(() => {
    if (!wantsSignup || signupPrompted.current) return;
    signupPrompted.current = true;
    setAuthOpen(true);
  }, [wantsSignup]);

  const finish = (destination: string) => {
    dismissWelcome();
    navigate(destination, { replace: true });
  };

  const handleLocate = () => {
    if (!regionsData) return;
    if (!navigator.geolocation) {
      setLocateState({ status: "error", message: "Geolocation not available." });
      return;
    }
    setLocateState({ status: "locating" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const nearest = findNearestRegion(
          pos.coords.latitude,
          pos.coords.longitude,
          regionsData,
        );
        if (!nearest) {
          setLocateState({
            status: "error",
            message: "No breaks near you yet — more coasts coming.",
          });
          trackWelcomeLocateError("no-nearby-break", campaign ?? "direct");
          return;
        }
        trackWelcomeLocateSuccess(nearest.regionId, campaign ?? "direct");
        setActiveRegion(nearest.regionId);
        setActiveCameraId(nearest.cameraId);
        const camPath = nearest.cameraId
          ? `/${encodeURIComponent(nearest.cameraId)}`
          : "";
        finish(`/spot/${encodeURIComponent(nearest.regionId)}${camPath}`);
      },
      (err) => {
        const reason =
          err.code === err.PERMISSION_DENIED ? "permission-denied" : "geolocation-failed";
        setLocateState({
          status: "error",
          message:
            err.code === err.PERMISSION_DENIED
              ? "Location permission denied."
              : "Couldn't get your location.",
        });
        trackWelcomeLocateError(reason, campaign ?? "direct");
      },
      { timeout: 8000, enableHighAccuracy: false },
    );
  };

  const handleSignIn = () => {
    trackWelcomeSigninClick(campaign ?? "direct");
    setAuthOpen(true);
  };

  // If user is already authenticated by the time the page mounts (e.g.
  // they returned from a redirect-auth flow), treat this as completion
  // and drop them into the app.
  useEffect(() => {
    if (user) {
      dismissWelcome();
    }
  }, [user]);

  const headline = isLegacyCampaign ? "Welcome back." : "How is the surf?";
  const subhead = isLegacyCampaign
    ? "howisthe.surf had a full rebuild. Pick your break, or sign in to post reports."
    : "Ensemble swell, wind, and tide for any coast. Live cameras. Reports from the water. Sign in to save your spots and post what you see.";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ pt: { xs: 8, md: 14 }, pb: { xs: 6, md: 10 } }}>
        {/* Wordmark */}
        <Typography
          sx={{
            fontFamily: fontStack.mono,
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "ink.muted",
            mb: { xs: 6, md: 10 },
          }}
        >
          howisthe.surf
        </Typography>

        {/* Hero */}
        <Typography
          component="h1"
          sx={{
            fontFamily: fontStack.serif,
            fontWeight: 500,
            fontSize: { xs: 42, md: 64 },
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
            color: "ink.main",
            mb: 2,
          }}
        >
          {headline}
        </Typography>
        <Typography
          sx={{
            fontFamily: fontStack.sans,
            fontSize: { xs: 17, md: 20 },
            lineHeight: 1.45,
            color: "ink.muted",
            maxWidth: 620,
            mb: { xs: 5, md: 7 },
          }}
        >
          {subhead}
        </Typography>

        {/* Primary CTAs */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={
              locateState.status === "locating" ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <MyLocationIcon />
              )
            }
            disabled={locateState.status === "locating" || !regionsData}
            onClick={handleLocate}
            sx={{
              fontFamily: fontStack.sans,
              fontWeight: 500,
              textTransform: "none",
              px: 3,
              py: 1.25,
              borderRadius: 1,
            }}
          >
            {locateState.status === "locating" ? "Finding your break…" : "Find my break"}
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<LoginIcon />}
            onClick={handleSignIn}
            sx={{
              fontFamily: fontStack.sans,
              fontWeight: 500,
              textTransform: "none",
              px: 3,
              py: 1.25,
              borderRadius: 1,
              borderColor: "rule.strong",
              color: "ink.main",
            }}
          >
            Sign in
          </Button>
        </Box>

        {/* Tertiary: take-a-look skip. Soft gate — the framing sets the
            expectation that a full-feature experience will ask for an
            account soon after. Paired with the broader in-app gate that
            triggers on a key interaction (see ROADMAP). */}
        <Button
          variant="text"
          onClick={() => {
            trackWelcomeSkipClick(campaign ?? "direct");
            finish("/");
          }}
          sx={{
            fontFamily: fontStack.sans,
            fontSize: 14,
            color: "ink.muted",
            textTransform: "none",
            pl: 0,
            "&:hover": { bgcolor: "transparent", color: "ink.main" },
          }}
        >
          Take a look first →
        </Button>

        {locateState.status === "error" && (
          <Typography
            sx={{
              mt: 2,
              fontFamily: fontStack.mono,
              fontSize: 13,
              color: "buoy.main",
            }}
          >
            {locateState.message}
          </Typography>
        )}

        {/* What's in it */}
        <Box
          sx={{
            mt: { xs: 8, md: 12 },
            pt: 4,
            borderTop: "1px solid",
            borderColor: "rule.main",
          }}
        >
          <Typography
            sx={{
              fontFamily: fontStack.mono,
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "ink.muted",
              mb: 3,
            }}
          >
            What's in it
          </Typography>

          <Box sx={{ display: "grid", gap: 3 }}>
            <Feature
              title="Global forecasts"
              body="3-model ensemble swell, wind, tide, gusts — one view."
            />
            <Feature
              title="Post from anywhere"
              body="Quick session reports, thumb up or down, done."
            />
            <Feature
              title="Portugal beachcams"
              body="Every public cam on the coast, in one grid."
            />
            <Feature title="No ads. No paywall. No login to read." body={null} />
          </Box>
        </Box>

        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            pt: 3,
            borderTop: "1px solid",
            borderColor: "rule.main",
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            fontFamily: fontStack.mono,
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "ink.muted",
          }}
        >
          <a href="/privacy" style={{ color: "inherit", textDecoration: "none" }}>
            Privacy
          </a>
          <a href="/terms" style={{ color: "inherit", textDecoration: "none" }}>
            Terms
          </a>
        </Box>
      </Container>
      <AuthPromptDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        initialMode={wantsSignup ? "signup" : "signin"}
        source="welcome"
        reason={
          wantsSignup
            ? "Create an account with your email or Google to save your break and post reports."
            : "Sign in with your email or Google to save your break and post reports."
        }
      />
    </Box>
  );
}

function Feature({ title, body }: { title: string; body: string | null }) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: fontStack.serif,
          fontWeight: 500,
          fontSize: 20,
          color: "ink.main",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {body && (
        <Typography
          sx={{
            fontFamily: fontStack.sans,
            fontSize: 15,
            color: "ink.muted",
            lineHeight: 1.55,
          }}
        >
          {body}
        </Typography>
      )}
    </Box>
  );
}
